import { useState, useRef, useEffect } from "react";
import CustomForm from "../components/CustomForm";
import { useDispatch } from "react-redux";
import { budatasliceactions } from "../store/budata-slice";
import { useNavigate } from "react-router-dom";
import { notificationsliceactions } from "../store/notification";
import { loadingsliceactions } from "../store/loading-slice";
import { usHttp } from "../custom-hooks/use-http";
import appvariables from "../appvariables";
import { setupRecaptcha, sendOTP, verifyOTP, cleanupAuth } from "../firebase/phoneAuth";
import Button from "../components/Button";

const Loginpage = () => {
    const [logindata, setLogindata] = useState('');
    const logindataref = useRef();
    const [loggedin, setLoggedin] = useState(false);
    const [initial, setInitial] = useState(true);
    const navigate = useNavigate();

    // New state for mobile OTP login
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState('');
    const [loginStep, setLoginStep] = useState('phone'); // 'phone' or 'otp'
    const [userName, setUserName] = useState('');
    const [isPhoneVerified, setIsPhoneVerified] = useState(false);
    
    const phoneNumberRef = useRef();
    const otpRef = useRef();

    const masterdispatcher = useDispatch();

    const reloadpage = () => {
        navigate('/');
        masterdispatcher(notificationsliceactions.updatenotification({message: 'Login Successful', status:'Success'}))
    }

    function getCookie(cname) {
        let name = cname + "=";
        let decodedCookie = decodeURIComponent(document.cookie);
        let ca = decodedCookie.split(';');
        for(let i = 0; i <ca.length; i++) {
          let c = ca[i];
          while (c.charAt(0) === ' ') {
            c = c.substring(1);
          }
          if (c.indexOf(name) === 0) {
            return c.substring(name.length, c.length);
          }
        }
        return "";
    }

    // Setup reCAPTCHA when component mounts
    useEffect(() => {
        setupRecaptcha('recaptcha-container');
        
        // Cleanup on unmount
        return () => {
            cleanupAuth();
        };
    }, []);

    // Handler for phone number input
    const handlePhoneNumberChange = () => {
        const phone = phoneNumberRef.current.value;
        setPhoneNumber(phone);
    };

    // Handler for OTP input
    const handleOtpChange = () => {
        const otpValue = otpRef.current.value;
        setOtp(otpValue);
    };

    // Step 1: Verify phone number with backend
    const handlePhoneSubmit = async (e) => {
        e.preventDefault();
        
        if (!phoneNumber || phoneNumber.length !== 10) {
            masterdispatcher(notificationsliceactions.updatenotification({
                message: 'Please enter a valid 10-digit mobile number',
                status: 'Error'
            }));
            return;
        }

        masterdispatcher(loadingsliceactions.updateloading({state: 'loading'}));

        try {
            const response = await usHttp(
                appvariables.apiurl + 'billinguser/verify-phone/',
                'POST',
                { phonenumber: phoneNumber }
            );

            masterdispatcher(loadingsliceactions.updateloading({state: ''}));

            if (response.status === 'Success') {
                setUserName(response.user_name);
                setIsPhoneVerified(true);
                
                // Send OTP via Firebase
                const otpResult = await sendOTP(phoneNumber);
                
                if (otpResult.success) {
                    setLoginStep('otp');
                    masterdispatcher(notificationsliceactions.updatenotification({
                        message: `OTP sent to ${phoneNumber}`,
                        status: 'Success'
                    }));
                } else {
                    masterdispatcher(notificationsliceactions.updatenotification({
                        message: otpResult.message,
                        status: 'Error'
                    }));
                }
            } else {
                masterdispatcher(notificationsliceactions.updatenotification({
                    message: response.message,
                    status: 'Error'
                }));
            }
        } catch (error) {
            masterdispatcher(loadingsliceactions.updateloading({state: ''}));
            masterdispatcher(notificationsliceactions.updatenotification({
                message: 'Network error. Please try again.',
                status: 'Error'
            }));
        }
    };

    // Step 2: Verify OTP and complete login
    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        
        if (!otp || otp.length !== 6) {
            masterdispatcher(notificationsliceactions.updatenotification({
                message: 'Please enter a valid 6-digit OTP',
                status: 'Error'
            }));
            return;
        }

        masterdispatcher(loadingsliceactions.updateloading({state: 'loading'}));

        try {
            // Verify OTP with Firebase
            const otpResult = await verifyOTP(otp);
            
            if (otpResult.success) {
                // Complete login with Firebase ID token
                const loginResponse = await usHttp(
                    appvariables.apiurl + 'billinguser/login-phone/',
                    'POST',
                    { 
                        id_token: otpResult.idToken,
                        phonenumber: phoneNumber
                    }
                );

                masterdispatcher(loadingsliceactions.updateloading({state: ''}));

                if (loginResponse.status === 'Success') {
                    // Set cookie and login
                    document.cookie = `cca=${loginResponse.cca}; expires=Sat, 12 Dec 2026 12:00:00 UTC; domain=${appvariables.url};path=/`;
                    masterdispatcher(budatasliceactions.updatebucca(loginResponse.cca));
                    reloadpage();
                } else {
                    masterdispatcher(notificationsliceactions.updatenotification({
                        message: loginResponse.message,
                        status: 'Error'
                    }));
                }
            } else {
                masterdispatcher(loadingsliceactions.updateloading({state: ''}));
                masterdispatcher(notificationsliceactions.updatenotification({
                    message: otpResult.message,
                    status: 'Error'
                }));
            }
        } catch (error) {
            masterdispatcher(loadingsliceactions.updateloading({state: ''}));
            masterdispatcher(notificationsliceactions.updatenotification({
                message: 'Network error. Please try again.',
                status: 'Error'
            }));
        }
    };

    // Resend OTP
    const handleResendOTP = async () => {
        masterdispatcher(loadingsliceactions.updateloading({state: 'loading'}));
        
        const otpResult = await sendOTP(phoneNumber);
        
        masterdispatcher(loadingsliceactions.updateloading({state: ''}));
        
        if (otpResult.success) {
            masterdispatcher(notificationsliceactions.updatenotification({
                message: 'OTP resent successfully',
                status: 'Success'
            }));
        } else {
            masterdispatcher(notificationsliceactions.updatenotification({
                message: otpResult.message,
                status: 'Error'
            }));
        }
    };

    // Go back to phone number step
    const handleBackToPhone = () => {
        setLoginStep('phone');
        setOtp('');
        setIsPhoneVerified(false);
        cleanupAuth();
        setupRecaptcha('recaptcha-container');
    };

    if(!loggedin && initial)
    {
        setInitial(false);
        if(getCookie('cca').length)
        {
            setLoggedin(true);
        }
    }

    const logindatainputhandler = () => {
        var cca = logindataref.current.value;
        setLogindata(cca);
    }

    const addccadatatocookie = (e) => {
        e.preventDefault();

        document.cookie = "cca=" + logindata + `; expires=Sat, 12 Dec 2026 12:00:00 UTC; domain=${appvariables.url};path=/`;
        setLogindata('');
        masterdispatcher(budatasliceactions.updatebucca(getCookie('cca')));
        reloadpage();
    }

    // Old login inputs (fallback)
    const inputs = [
        {
            displayname: 'Login Data',
            name:'logindata',
            type: 'text',
            refvar: logindataref,
            changehandler: logindatainputhandler,
        }
    ]

    // Phone number input configuration
    const phoneInputs = [
        {
            displayname: 'Mobile Number',
            name: 'phonenumber',
            type: 'text',
            refvar: phoneNumberRef,
            changehandler: handlePhoneNumberChange,
            placeholder: 'Enter 10-digit mobile number',
            maxLength: 10
        }
    ];

    // OTP input configuration
    const otpInputs = [
        {
            displayname: 'Enter OTP',
            name: 'otp',
            type: 'text',
            refvar: otpRef,
            changehandler: handleOtpChange,
            placeholder: 'Enter 6-digit OTP',
            maxLength: 6
        }
    ];

    if (loggedin) {
        return <div>Logged in as {getCookie('cca')}</div>;
    }

    return (
        <div style={{ maxWidth: '400px', margin: '0 auto', padding: '20px' }}>
            {loginStep === 'phone' ? (
                <div>
                    <h2>Login with Mobile Number</h2>
                    <p>Enter your registered mobile number to receive OTP</p>
                    <CustomForm 
                        inputs={phoneInputs} 
                        inputboxstyle={'normal'} 
                        submitHandler={handlePhoneSubmit}
                        submitText="Send OTP"
                    />
                </div>
            ) : (
                <div>
                    <h2>Verify OTP</h2>
                    <p>OTP sent to {phoneNumber}</p>
                    {userName && <p>Welcome back, {userName}!</p>}
                    
                    <CustomForm 
                        inputs={otpInputs} 
                        inputboxstyle={'normal'} 
                        submitHandler={handleOtpSubmit}
                        submitText="Verify OTP"
                    />
                    
                    <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                        <Button 
                            onClick={handleResendOTP} 
                            type="button"
                            style={{ backgroundColor: '#2196F3', color: 'white' }}
                        >
                            Resend OTP
                        </Button>
                        <Button 
                            onClick={handleBackToPhone} 
                            type="button"
                            style={{ backgroundColor: '#757575', color: 'white' }}
                        >
                            Change Number
                        </Button>
                    </div>
                </div>
            )}
            
            {/* reCAPTCHA container - hidden */}
            <div id="recaptcha-container" style={{ display: 'none' }}></div>
        </div>
    )
}

export default Loginpage;